import './DeckSidePanel.scss';

import { PermissionLevel, useUserContext } from '@context/UserContext';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNew from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useColorScheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResourceLink } from 'types/Common.types';

/**
 * Auriga resource links to be expanded as needed.
 * This side panel content is currently static and not dynamic,
 * and loads on the Landing page.
 */
const RESOURCE_LINKS: ResourceLink[] = [
  {
    id: 'kpi-lineage',
    label: 'KPI Lineage',
    url: 'https://cognos.corpprod.fpa.amazon.dev/bi/?pathRef=.public_folders%2FRFA%2BExecutive%2BDeck%2FOperational%2BReports%2FDeck%2BKPI%2BLineage%2BReport',
    ariaLabel: 'View KPI Lineage documentation',
  },
  {
    id: 'dimension-guide',
    label: 'Dimension Guide',
    url: 'https://us-west-2.quicksight.aws.amazon.com/sn/account/gfs-cognos-bi-aws-account/dashboards/711e36f4-86a6-43fd-8e11-e0e3a09423ed/sheets/711e36f4-86a6-43fd-8e11-e0e3a09423ed_00d0e14b-f5ab-4370-82ba-b55cc5be889b',
    ariaLabel: 'View Dimension Guide documentation',
  },
  {
    id: 'dimension-change-tracker',
    label: 'Dimension Change Tracker',
    url: 'https://us-west-2.quicksight.aws.amazon.com/sn/accounts/173245911106/dashboards/aacde0fb-221a-45e6-959b-6bd2a4b354b3?directory_alias=gfs-cognos-bi-aws-account',
    ariaLabel: 'View Dimension Change Tracker',
  },
  {
    id: 'previous-decks',
    label: 'Previous Published Decks',
    url: 'https://amazon.awsapps.com/workdocs-amazon/index.html#/folder/b22f2f79cfa13b8a7b07a3177edb0121d10e032b9d73f896a855067f5476de9e',
    ariaLabel: 'View Previously Published Decks',
  },
];

/**
 * Landing Side Panel Component
 *
 * Displays a list of useful resource links in the side panel of the landing page.
 * Each link opens in a new tab and includes an icon to indicate external navigation.
 */
const DeckSidePanel = () => {
  const navigate = useNavigate();
  const { hasPermissionLevel } = useUserContext();
  const { mode, setMode } = useColorScheme();
  const isDevTeamAdmin = hasPermissionLevel(PermissionLevel.DEV_TEAM_ADMIN);
  const [previewMode, setPreviewMode] = useState(() => localStorage.getItem('auriga-preview-mode') === 'true');

  /**
   * Handles the click event on a resource link.
   * Opens the link in a new tab.
   * @param url - String link of the external resource
   */
  const handleLinkClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box className='deck-side-panel'>
      <Typography variant='h6' className='resources-title'>
        Resources
      </Typography>
      <List className='resources-list'>
        {RESOURCE_LINKS.map(({ id, label, url, ariaLabel }) => (
          <ListItemButton
            key={id}
            onClick={() => handleLinkClick(url)}
            className='resource-item'
            aria-label={ariaLabel}
          >
            <ListItemText primary={label} className='resource-text' />
            <ListItemIcon className='resource-icon'>
              <OpenInNew />
            </ListItemIcon>
          </ListItemButton>
        ))}
      </List>
      <Divider className='resources-divider' />
      {isDevTeamAdmin && (
        <>
          <Typography variant='h6' className='resources-title'>
            Admin
          </Typography>
          <List className='resources-list'>
            <ListItemButton
              onClick={() => navigate('/deck-preparation/kpi-library')}
              className='resource-item'
              aria-label='Navigate to KPI Library'
            >
              <ListItemText primary='KPI Library' className='resource-text' />
              <ListItemIcon className='resource-icon'>
                <LinkIcon />
              </ListItemIcon>
            </ListItemButton>
            <ListItemButton
              className='resource-item'
              onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
              aria-label='Toggle dark mode'
            >
              <ListItemText primary='Dark Mode' className='resource-text' />
              <ListItemIcon className='resource-icon'>
                <Switch
                  checked={mode === 'dark'}
                  size='small'
                  icon={<LightModeIcon fontSize='small' />}
                  checkedIcon={<DarkModeIcon fontSize='small' />}
                />
              </ListItemIcon>
            </ListItemButton>
            <ListItemButton
              className='resource-item'
              onClick={() => {
                const next = !previewMode;
                localStorage.setItem('auriga-preview-mode', String(next));
                setPreviewMode(next);
              }}
              aria-label='Toggle preview mode'
            >
              <ListItemText primary='Preview Mode' className='resource-text' />
              <ListItemIcon className='resource-icon'>
                <Switch checked={previewMode} size='small' />
              </ListItemIcon>
            </ListItemButton>
          </List>
        </>
      )}
    </Box>
  );
};

export default DeckSidePanel;
